import React, { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { LoginV2Presenter } from './login-v2-presenter'
import { signInSchema as loginSchema } from '../../lib/utils/schema'
import { userServices } from '../../services'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch, useStore } from 'react-redux'
import { createConfiguration, updateConfiguration } from '../../global-state/investor-slice'
import { showBanner } from '../../global-state/banner-slice'
import { Link, useNavigate } from 'react-router-dom'
import { EVENTS } from '../../lib/utils/constants'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { getInvestorDataFromSS } from '../../lib/utils/local-storage'
import { applyPostLoginNavigation, resolvePostLoginDestination } from '../../lib/utils/post-login'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { Role, Verification, UtmAttributionPayload } from '../../lib/types'
import type { RootState } from '../../store'
import { getStoredUtmAttribution, toUtmAttributionPayload } from '../../lib/utils/utm-attribution'
import {
  completeEmailVerificationLogin,
  getVerifyEmailError,
  isEmailVerificationLoginData,
} from '../../lib/utils/post-verification-auth'
import { CBLogoV2 } from '../../lib/components/Icon'
import { LoginEmailVerificationPanel, OTP_LENGTH } from './login-email-verification-panel'
import './login-v2.scss'

const initialValues = {
  email: '',
  password: '',
  rememberMe: '',
}

const LoginV2Container = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const reRef = useRef<ReCAPTCHA>(null)
  const { authToken, investorData } = getInvestorDataFromSS()
  const [pendingVerification, setPendingVerification] = useState<{
    email: string
    userId?: number
  } | null>(null)
  const [otpDigits, setOtpDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''))
  const [isResendingVerification, setIsResendingVerification] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    const { authToken: sessionToken, investorData: sessionInvestor } = getInvestorDataFromSS()
    const { investor: investorState } = store.getState()
    const token = investorState.authToken || sessionToken
    if (!token) return

    let cancelled = false
    ;(async () => {
      const session = await resolvePostLoginDestination(
        token,
        investorState.userId || sessionInvestor?.userId,
        investorState.email || sessionInvestor?.email,
        investorState.name || sessionInvestor?.name,
        investorState.acceptedNDA ?? sessionInvestor?.acceptedNDA ?? false,
      )
      if (!cancelled) {
        navigate(session.destination, { replace: true })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [navigate, store])

  const focusOtpInput = (index: number) => {
    otpInputRefs.current[index]?.focus()
  }

  const handleOtpChange = (index: number, value: string) => {
    const parsed = value.replace(/\D/g, '').slice(-1)
    const next = [...otpDigits]
    next[index] = parsed
    setOtpDigits(next)
    if (parsed && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1)
    }
  }

  const handleOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      focusOtpInput(index - 1)
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      focusOtpInput(index - 1)
    }
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1)
    }
  }

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const next = Array(OTP_LENGTH)
      .fill('')
      .map((_, i) => pasted[i] || '')
    setOtpDigits(next)
    focusOtpInput(Math.min(pasted.length, OTP_LENGTH - 1))
  }

  const handleVerifyEmailOtp = useCallback(async () => {
    const userId = pendingVerification?.userId
    if (userId === null) return
    const otp = otpDigits.join('')
    if (otp.length !== OTP_LENGTH) {
      dispatch(showBanner({ text: 'Please enter the 6-digit code.', variant: 'error-banner' }))
      return
    }
    setIsVerifyingOtp(true)
    const response = await userServices.verifyEmail_v1(
      otp,
      String(userId),
      Verification.verifyEmail,
    )
    setIsVerifyingOtp(false)

    if (response?.status === 200 && isEmailVerificationLoginData(response.data)) {
      captureEvent(EVENTS.email_verified, { user: userId })
      dispatch(
        showBanner({
          text: 'Email verified successfully.',
          variant: 'success-banner',
        }),
      )
      completeEmailVerificationLogin(dispatch, response.data)
      setPendingVerification(null)
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      await applyPostLoginNavigation(
        dispatch,
        navigate,
        response.data.authToken,
        response.data.userId,
        response.data.email,
        response.data.name,
        response.data.acceptedNDA ?? false,
      )
      return
    }

    dispatch(
      showBanner({
        text:
          getVerifyEmailError(response?.data)?.message ||
          'Invalid or expired code. Try again or resend the email.',
        variant: 'error-banner',
      }),
    )
  }, [pendingVerification?.userId, otpDigits, dispatch, navigate])

  const handleResendVerification = useCallback(async () => {
    const email = pendingVerification?.email
    if (!email) return
    setIsResendingVerification(true)
    try {
      const response = await userServices.sendVerificationLink_v1(email, Verification.verifyEmail)
      if (!!response && response.status === 200) {
        captureEvent(EVENTS.email_verification_link_sent, { email })
        dispatch(
          showBanner({
            text: 'Verification email sent. Please check your inbox or spam folder.',
            variant: 'success-banner',
          }),
        )
        const uid = (response.data as { userId?: number } | undefined)?.userId
        if (typeof uid === 'number') {
          setPendingVerification((prev) =>
            prev ? { ...prev, userId: uid } : { email, userId: uid },
          )
        }
        setOtpDigits(Array(OTP_LENGTH).fill(''))
        window.setTimeout(() => focusOtpInput(0), 0)
      } else {
        dispatch(
          showBanner({
            text:
              (response?.data && response.data.message) ||
              'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsResendingVerification(false)
    }
  }, [pendingVerification?.email, dispatch])

  const handleSubmit_v1 = async (val: {
    email?: string
    password?: string
    rememberMe?: string
  }) => {
    const token = await reRef.current?.executeAsync()
    reRef.current?.reset()
    setPendingVerification(null)
    setOtpDigits(Array(OTP_LENGTH).fill(''))
    if (!authToken && !investorData) {
      const user = await userServices.userLogin_v1(
        {
          email: (val.email ?? '').toLowerCase(),
          password: val.password ?? '',
          role: Role.investor,
        },
        { 'g-recaptcha-response': token ? token : '' },
      )
      if (!!user && user.status === 200 && user?.data?.role === Role.investor) {
        // Unverified investors are signed in too: they get the verify-email banner and onboarding.
        const session = await resolvePostLoginDestination(
          user.data.authToken,
          user.data.userId,
          user.data.email,
          user.data.name,
          user.data.acceptedNDA ?? false,
        )

        flushSync(() => {
          navigate(session.destination)
        })

        dispatch(
          createConfiguration({
            isEmailVerified: user.data.is_verified,
            accountStatus: user.data.account_status,
            ...user.data,
            rememberMe: val.rememberMe ? true : false,
            basicDetailsCompleted: session.basicDetailsCompleted,
            riskProfileCompleted: session.riskProfileCompleted,
            riskProfilerRequired: session.riskProfilerRequired,
          }),
        )

        dispatch(
          showBanner({
            text: 'Logged in successfully',
            variant: 'success-banner',
            duration: 1000,
          }),
        )
        captureEvent(EVENTS.user_logged_in, {
          email: val.email,
        })

        logCrmEvent(
          EngagementEvents.USER_LOGGED_IN_EMAIL,
          { Authorization: user.data.authToken },
          { email: user.data.email },
        )
      } else if (!!user && user.status === 200 && user?.data?.role !== Role.investor) {
        dispatch(showBanner({ text: 'Not allowed to login!', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data?.code === 'wrong-user-source') {
        dispatch(showBanner({ text: 'Try login using Google', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data?.code === 'email-not-verified') {
        const emailLower = (val.email ?? '').toLowerCase()
        const uid = (user.data as { userId?: number }).userId
        setPendingVerification({
          email: emailLower,
          ...(typeof uid === 'number' ? { userId: uid } : {}),
        })
        setOtpDigits(Array(OTP_LENGTH).fill(''))
      } else {
        if (user.data?.code === 'account-blocked') {
          dispatch(
            showBanner({
              text: (
                <div data-testid='login-blocked-user-warning'>
                  We have temporarily blocked your profile for security reasons. If this was not
                  intended, please contact us at &nbsp;
                  <a className='basic-anchor' href='mailto:support@credibila.com'>
                    support@credibila.com
                  </a>
                  &nbsp; for assistance.
                </div>
              ),
              variant: 'error-banner',
              duration: 15000,
            }),
          )
        } else {
          dispatch(
            showBanner({
              text: user.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } else {
      dispatch(
        showBanner({
          text: 'You already logged in another tab. Please logout and use another account.',
          variant: 'error-banner',
          duration: 3000,
        }),
      )
    }
  }

  const googleLoginHandler = async (res: object, rememberMe?: unknown) => {
    const { access_token } = res as { access_token: string }
    if (!authToken && !investorData) {
      const utmAttribution: UtmAttributionPayload | undefined = toUtmAttributionPayload(
        getStoredUtmAttribution(),
      )
      const user = await userServices.googleSignIn_v1({
        accessToken: access_token,
        role: 'INVESTOR',
        createIfMissing: false,
        ...(utmAttribution ? { utmAttribution } : {}),
      })
      if (!!user && user.status === 200 && user?.data?.role === 'INVESTOR') {
        if (user.data.isEmailVerified) {
          const session = await resolvePostLoginDestination(
            user.data.authToken,
            user.data.userId,
            user.data.email,
            user.data.name,
            user.data.acceptedNDA ?? false,
          )

          flushSync(() => {
            navigate(session.destination)
          })

          dispatch(
            createConfiguration({
              ...user.data,
              accountStatus: user.data.account_status,
              rememberMe: rememberMe,
              basicDetailsCompleted: session.basicDetailsCompleted,
              riskProfileCompleted: session.riskProfileCompleted,
              riskProfilerRequired: session.riskProfilerRequired,
            }),
          )

          dispatch(
            showBanner({
              text: 'Logged in successfully',
              variant: 'success-banner',
              duration: 1000,
            }),
          )

          logCrmEvent(
            EngagementEvents.USER_LOGGED_IN_SOCIAL,
            { Authorization: user.data.authToken },
            { email: user.data.email, socialPlatform: 'google' },
          )
        } else {
          dispatch(
            createConfiguration({
              ...user.data,
              accountStatus: user.data.account_status,
              rememberMe: rememberMe,
            }),
          )
        }
      } else if (!!user && user.status === 200 && user?.data?.role !== 'INVESTOR') {
        dispatch(showBanner({ text: 'Not allowed to login!', variant: 'error-banner' }))
      } else if (!!user && user.status === 401 && user.data?.code === 'no-google-account') {
        dispatch(
          showBanner({
            text: 'No account found. Please sign up with Google first.',
            variant: 'error-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: (user.data && user.data.message) || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } else {
      dispatch(
        showBanner({
          text: 'You already logged in another tab. Please logout and use another account.',
          variant: 'error-banner',
        }),
      )
      window.location.reload()
    }
  }

  return (
    <div className='login-page' data-testid='login-page'>
      <aside className='login-page__brand' aria-label='Credibila login'>
        <header className='login-page__brand-header'>
          <h1 className='login-page__brand-header-title'>Welcome Back!</h1>
        </header>
        <div
          className='login-page__brand-hero'
          role='img'
          aria-label='Client reviewing markets on a tablet'
        />
        <section
          className='login-page__brand-stats'
          aria-label='Key statistics'
          data-testid='login-panel-stats'
        >
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>6+ Countries</div>
            <div className='login-page__stat-label'>Global Presence</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>3000+</div>
            <div className='login-page__stat-label'>Strong Community</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>80+ Years</div>
            <div className='login-page__stat-label'>Combined Team Experience</div>
          </div>
          <div className='login-page__stat-block'>
            <div className='login-page__stat-value'>$25+ Million</div>
            <div className='login-page__stat-label'>AUM</div>
          </div>
        </section>
      </aside>
      <main className='login-page__form-section' aria-label='Login form'>
        <div className='login-page__form-card'>
          <Link to='/home' className='login-page__form-logo' aria-label='Go to home'>
            <CBLogoV2 classes={['login-page__form-logo-svg']} width='168' height='40' />
          </Link>
          <LoginV2Presenter
            initialValues={initialValues}
            validationSchema={loginSchema}
            handleFormSubmit={handleSubmit_v1}
            reRef={reRef}
            googleLoginHandler={googleLoginHandler}
          />
          {pendingVerification && (
            <LoginEmailVerificationPanel
              pending={pendingVerification}
              otpDigits={otpDigits}
              onOtpChange={handleOtpChange}
              onOtpKeyDown={handleOtpKeyDown}
              onOtpPaste={handleOtpPaste}
              onVerify={handleVerifyEmailOtp}
              onResend={handleResendVerification}
              isResending={isResendingVerification}
              isVerifying={isVerifyingOtp}
              setOtpInputRef={(index, el) => {
                otpInputRefs.current[index] = el
              }}
            />
          )}
        </div>
      </main>
    </div>
  )
}

export { LoginV2Container }
